import { Button } from '@components/core-ui/form-elements/buttons/Button';
import LoadingIcon from '@components/icons/Loading.svg';
import { cn } from '@helpers/cn';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { SubscriptionsService, type GetBillingAccountResponseSubscriptionDto } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useRetrySubscriptionPayment } from 'app/hooks/useRetrySubscriptionPayment';

type TProps = {
  subscription: GetBillingAccountResponseSubscriptionDto;
  billingAccountId: string;
  reload: () => void;
  className?: string;
};

export const PaymentFailedCard = ({ billingAccountId, className, reload, subscription }: TProps) => {
  const { organization } = useAuthContext();
  const { closeModal, openModal } = useModalContext();
  const { call } = useAPI();

  const retry = () =>
    call(() => SubscriptionsService.retryPayment({ baId: billingAccountId, subscriptionId: subscription.id }), {
      dontRedirectOnError: true,
    });

  const { isPending, retryPayment } = useRetrySubscriptionPayment({
    onPaid: reload,
    onUpdatePaymentMethod: organization?.permissions?.manageBilling
      ? () => {
          openModal(ModalNamesEnum.AddPaymentMethod, {
            onSuccess: () => {
              closeModal();
              retryPayment();
            },
          });
        }
      : undefined,
    retry,
  });

  const canRetry = organization?.permissions?.manageLicenses && subscription.status !== 'incomplete_expired';

  const onRetryClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    retryPayment();
  };

  return (
    <div
      className={cn(
        'flex min-h-[220px] w-[230px] flex-col rounded-[10px] bg-[var(--color-surface)] p-5 shadow-[0_8px_10px_var(--color-shadow)]',
        className,
      )}
    >
      <h2 className="m-0 text-center text-[18px] font-medium leading-[150%] text-[var(--color-text-muted)]">
        Payment Failed
      </h2>
      <p className="m-0 pt-2.5 text-sm leading-[150%] tracking-[0.42px] text-[var(--color-text-muted)]">
        Your AutoScript One subscription is temporarily disabled. Please try again or use another payment method.
      </p>
      <p className="m-0 pt-2.5 text-sm leading-[150%] tracking-[0.42px] text-[var(--color-text-muted)]">
        For assistance, please contact{' '}
        <a className="font-bold" href="mailto:support@voicescript.ai" onClick={(event) => event.stopPropagation()}>
          support@voicescript.ai
        </a>
        .
      </p>
      {canRetry ? (
        <Button
          aria-busy={isPending}
          className="btn--md mt-auto self-stretch"
          disabled={isPending}
          onClick={onRetryClick}
        >
          {isPending ? <LoadingIcon className="h-3 w-3 animate-spin" /> : null}
          Retry
        </Button>
      ) : null}
    </div>
  );
};
